/* eslint-disable react-hooks/exhaustive-deps */
import { SearchOutlined } from '@ant-design/icons';
import { Button, Form, Input, Select, Table, Tag } from 'antd';
import moment from 'moment';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsentService from '../../../services/Consent.service';
import ModalExport from './ModalExport';
import usePermission from '../../../hooks/usePermission';

const getTagDay = (t, e) => {
    if (e > 30) {
        return (
            <Tag className="rounded-xl -my-1" color="#1890FF">
                <div className="flex items-center gap-x-1">
                    <div>{e}</div>
                </div>
            </Tag>
        );
    } else if (e === null) {
        return (
            <Tag className="text-xs rounded-xl -my-1" color="#1890FF">
                {t('Consent.NoExpiry')}
            </Tag>
        );
    } else if (e <= 0) {
        return (
            <Tag className="text-xs rounded-xl -my-1" color="#F0536F">
                {e}
            </Tag>
        );
    } else if (e <= 7) {
        return (
            <Tag className="text-xs rounded-xl -my-1" color="#F9C153">
                {e}
            </Tag>
        );
    } else {
        return (
            <Tag className="text-xs rounded-xl -my-1" color="#1890FF">
                {e}
            </Tag>
        );
    }
};

const LogTable = memo(function LogTable({ refresh, setRefresh, periodObj }) {
    const { t, i18n } = useTranslation();
    const [result, setResult] = useState([]);
    const [values, setValues] = useState();
    const [currentPage, setCurrentPage] = useState({
        p: 1,
        ps: 10,
        s: 'consentDate',
        sd: 'desc',
        total: 0,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const lang = i18n.language;

    const _getTagAction = (e) => {
        if (e === 'Dismiss') {
            return (
                <Tag
                    className="rounded-xl -my-1 text-center"
                    style={{ color: '#65707A', borderColor: '#65707A' }}
                >
                    {t('COOKIE.Dismiss')}
                </Tag>
            );
        } else if (e === 'Informed') {
            return (
                <Tag
                    className="rounded-xl -my-1 text-center"
                    style={{ color: '#F9C153', borderColor: '#F9C153' }}
                >
                    {t('COOKIE.Informed')}
                </Tag>
            );
        } else if (e === 'Accepted') {
            return (
                <Tag
                    className="rounded-xl -my-1 text-center"
                    style={{ color: '#4ACE8F', borderColor: '#4ACE8F' }}
                >
                    {t('COOKIE.Accepted')}
                </Tag>
            );
        } else if (e === 'Rejected') {
            return (
                <Tag
                    className="rounded-xl -my-1 text-center"
                    style={{ color: '#F0536F', borderColor: '#F0536F' }}
                >
                    {t('COOKIE.Rejected')}
                </Tag>
            );
        } else {
            return <>-</>;
        }
    };

    const containerStyle = {
        width: '250px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
    };

    const columns = useMemo(() => {
        return [
            {
                title: t('Consent.ConsentDate'),
                dataIndex: 'consentDate',
                key: 'consentDate',
                // width: 160,
                align: 'center',
                sorter: true,
                render: (value) =>
                    value ? moment(value).format('DD/MM/YYYY') : '-',
            },
            {
                title: t('Consent.LogDate'),
                dataIndex: 'logDate',
                key: 'logDate',
                // width: 160,
                sorter: true,
                align: 'center',
                render: (value) =>
                    value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
            },

            {
                title: t('Consent.Identity'),
                dataIndex: 'identity',
                key: 'identity',
                // with: 154,
                sorter: true,
                render: (value) => {
                    return <>{value ? <div>{value}</div> : <>-</>}</>;
                },
            },
            {
                title: t('Consent.ConsentName'),
                dataIndex: lang === 'en' ? 'consentNameEn' : 'consentNameTh',
                key: lang === 'en' ? 'consentNameEn' : 'consentNameTh',
                sorter: true,
                render: (value) => {
                    return <>{value ? <div>{value}</div> : <>-</>}</>;
                },
            },
            {
                title: t('Consent.ConsentType'),
                dataIndex: 'consentType',
                key: 'consentType',
                sorter: true,
                // width: 150,
                render: (value) => {
                    return <>{value ? <div>{value}</div> : <>-</>}</>;
                },
            },
            {
                title: t('Consent.TempleteName'),
                dataIndex: lang === 'en' ? 'templateNameEn' : 'templateNameTh',
                key: lang === 'en' ? 'templateNameEn' : 'templateNameTh',
                sorter: true,
                // width: 150,
                render: (value) => {
                    return (
                        <>
                            {value ? (
                                <div
                                    dangerouslySetInnerHTML={{ __html: value }}
                                />
                            ) : (
                                <>-</>
                            )}
                        </>
                    );
                },
            },
            {
                title: t('Consent.CollectionPointName'),
                dataIndex: 'collectionPointName',
                key: 'collectionPointName',
                sorter: true,
                width: '250px',
                render: (value) => {
                    return (
                        <>
                            {value ? (
                                <div style={containerStyle}>{value}</div>
                            ) : (
                                <>-</>
                            )}
                        </>
                    );
                },
            },
            {
                title: t('Consent.DayLeft'),
                dataIndex: 'dayLeft',
                key: 'dayLeft',
                align: 'center',
                render: (value) => {
                    return getTagDay(t, value);
                },
            },
            {
                title: t('Consent.ApplicationRelated'),
                dataIndex: 'applicationRelated',
                key: 'applicationRelated',
                sorter: true,
                // width: 249,
                render: (applications) =>
                    applications?.length > 0
                        ? applications?.map((app) => (
                            <div
                                key={app}
                                className="bg-[#EDEEF9] rounded-md px-2 py-1 text-purple w-max font-medium inline-flex mr-2 mb-2"
                            >
                                {app}
                            </div>
                        ))
                        : '-',
            },
            {
                title: t('Consent.Status'),
                dataIndex: 'isAccept',
                key: 'isAccept',
                align: 'center',
                // width: 150,
                render: (isAccept) =>
                    _getTagAction(isAccept ? 'Accepted' : 'Rejected'),
            },
            {
                title: t('Consent.Version'),
                dataIndex: 'version',
                key: 'version',
                sorter: true,
                // width: 100,
                render: (value) => {
                    return <>{value ? <div>{value}</div> : <>-</>}</>;
                },
            },
        ];
    }, [lang, t]);

    const onValuesChange = async (_, values) => {
        values.isAccept = values.isAccept === 'all' ? null : values.isAccept;
        values.consentType =
            values.consentType === 'all' ? '' : values.consentType;
        setValues(values);
    };

    const onSearch = useCallback(
        async (currentPage) => {
            try {
                setIsLoading(true);
                const payload = {
                    ...periodObj,
                    ...values,
                    p: currentPage.p,
                    ps: currentPage.ps,
                    s: currentPage.s,
                    sd: currentPage.sd,
                };
                const { data } = await ConsentService.getConsentSearch(payload);
                if (data.resultData) {
                    setResult(data.resultData.result);
                    setCurrentPage({
                        p: data.resultData.page,
                        ps: data.resultData.pageSize,
                        total: data.resultData.total,
                    });
                    setRefresh(false);
                    setIsLoading(false);
                }
            } catch (error) {
                console.log(error);
                setIsLoading(false);
            }
        },
        [values]
    );

    useEffect(() => {
        if (refresh) {
            form.resetFields();
            setValues();
            onSearch({
                p: 1,
                ps: 10,
                s: 'consentDate',
                sd: 'desc',
                total: 0,
            });
        } else {
            onSearch({
                p: 1,
                ps: 10,
                s: 'consentDate',
                sd: 'desc',
                total: 0,
            });
        }
       
    }, [refresh, values]);

    useEffect(async () => {
        try {
            setIsLoading(true);
            const payload = {
                ...periodObj,
                ...values,
                p: currentPage.p,
                ps: currentPage.ps,
                s: currentPage.s,
                sd: currentPage.sd,
            };

            const { data } = await ConsentService.getConsentSearch(payload);
            if (data.resultData) {
                setResult(data.resultData.result);
                setCurrentPage({
                    p: data.resultData.page,
                    ps: data.resultData.pageSize,
                    total: data.resultData.total,
                });
                setRefresh(false);
                setIsLoading(false);
            }
        } catch (error) {
            console.log(error);
            setIsLoading(false);
        }
    }, [periodObj]);

    const onPageChange = async (pagination, filters, sorter) => {
        const pageValues = {
            p: pagination.current,
            ps: pagination.pageSize,
            s: sorter && sorter.order ? sorter.columnKey : 'consentDate',
            sd:
                sorter && sorter.order
                    ? sorter.order.substring(0, 3).toUpperCase() === 'ASC'
                        ? 'asc'
                        : 'desc'
                    : 'desc',
            total: currentPage.total,
        };
        setCurrentPage(pageValues);
        onSearch(pageValues);
    };

    const paginationProps = {
        current: parseInt(currentPage.p),
        // pageSize: 10,
        total: parseInt(currentPage.total),
        // showQuickJumper: true,
        showSizeChanger: true,
        showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
    };

    const [openModal, setOpenModal] = useState(false);

    const { isAuditor } = usePermission();

    return (
        <div className="col-span-2 bg-white p-4 rounded-lg">
            <div className="flex justify-between items-center">
                <div className="text-xl font-semibold">Consent Log</div>
                <Form
                    form={form}
                    layout="inline"
                    onValuesChange={onValuesChange}
                >
                    <Form.Item name="keyword">
                        <Input
                            className="w-80"
                            prefix={
                                <SearchOutlined className="text-[#65707A]" />
                            }
                            size="large"
                            placeholder="Identity, Consent Name, Collection Name"
                        />
                    </Form.Item>
                    <Form.Item name="consentType">
                        <Select
                            size="large"
                            defaultValue="all"
                            style={{ width: 257 }}
                            options={[
                                {
                                    label:
                                        lang === 'en'
                                            ? 'All Consent Type'
                                            : 'ประเภทความยินยอมทั้งหมด',
                                    value: 'all',
                                },
                                { label: 'Consent', value: 'Consent' },
                                {
                                    label: 'Term & Condition',
                                    value: 'Term & Condition',
                                },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item name="isAccept">
                        <Select
                            size="large"
                            defaultValue="all"
                            style={{ width: 257 }}
                            options={[
                                {
                                    label:
                                        lang === 'en'
                                            ? 'All Status'
                                            : 'สถานะทั้งหมด',
                                    value: 'all',
                                },
                                { label: t('COOKIE.Accepted'), value: true },
                                { label: t('COOKIE.Rejected'), value: false },
                            ]}
                        />
                    </Form.Item>

                    <Button
                        disabled={isAuditor}
                        autoFocus
                        className="px-9"
                        type="primary"
                        size="large"
                        onClick={() => setOpenModal(true)}
                    >
                        {t('common.Export')}
                    </Button>
                </Form>
            </div>
            <div
                className="w-full h-1 my-4"
                style={{ borderTop: '3px solid #F4F6FC' }}
            ></div>
            <Table
                loading={isLoading}
                onChange={onPageChange}
                rowKey={(record) => record.tconsentId}
                className="inbox_table"
                dataSource={result}
                columns={columns}
                pagination={paginationProps}
                scroll={{ x: 1600 }}
            />
            <ModalExport
                detail={values}
                open={openModal}
                handelClose={() => setOpenModal(false)}
            />
        </div>
    );
});

export default LogTable;
